import { useId, useState } from 'react'
import { router, usePage } from '@inertiajs/react'

import { Alert, Button, Link } from '@atoms'
import TextInput from '@components/atoms/form/TextInput'
import cn from '@components/utils/cn'
import { PageLayout } from '@components/layouts/PageLayout'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import { scopedTranslation } from '@utils/I18n'

import { ColorField } from '@components/embeds/ColorField'
import { EmbedCodeBlock } from '@components/embeds/EmbedCodeBlock'
import { EmbedErrorBoundary } from '@components/embeds/EmbedErrorBoundary'
import { Accordion } from '@components/atoms/Accordion'
import { EmbedWorkspace, SettingsAccordionSection, SettingsSection } from '@components/embeds/EmbedWorkspace'
import { EventFilters, type EventFilterOptions } from '@components/embeds/EventFilters'
import { EventsEmbed } from '@components/embeds/EventsEmbed'
import { PreviewShell } from '@components/embeds/PreviewShell'
import { ToggleRow } from '@components/embeds/ToggleRow'
import { ViewSwitch } from '@components/embeds/ViewSwitch'
import {
  MAX_TAG_FILTERS,
  MAX_WORDING_LENGTH,
  NEW_TAG_FILTER,
  RADIUS_OPTIONS,
  decodeEmbedConfig,
  encodeEmbedConfig,
  hasFilters,
  withoutFilters,
  type Corners,
  type EmbedConfig,
  type Layout,
  type LinkTarget,
  type Radius,
  type TagFilter,
  type View,
} from '@utils/embeds/embedConfig'
import { useEventsFeed, type EventsFeed } from '@utils/embeds/eventsFeed'
import { DEFAULT_FONT, FONTS } from '@utils/embeds/fonts'
import { offersLocationSearch, type PublicEvent } from '@utils/embeds/publicEvent'
import { sampleEvents } from '@utils/embeds/sampleData'
import { tagOptions } from '@utils/embeds/supporterFilters'
import { embedAddress, embedCode } from '@utils/embeds/embedCode'
import { defaultBrand, isReadableOnWhite } from '@utils/embeds/theme'

const t = scopedTranslation('embeds.events_map')
const tEmbed = scopedTranslation('embeds.events')
const tBrand = scopedTranslation('embeds.editor.brand')

const STEPS = ['settings', 'code'] as const

type Step = (typeof STEPS)[number]

const INSTALL_METHODS = ['code', 'iframe', 'address'] as const

type InstallMethod = (typeof INSTALL_METHODS)[number]

function handedOver(method: InstallMethod) {
  return method === 'address' ? 'address' : 'code'
}

const LAYOUTS: Layout[] = ['both', 'grid', 'map']

const FIRST_PIN_COLOR = '#B91C1C'

function customWording(text: string) {
  return text.trim() ? text : null
}

function fontLabel({ family, kind }: (typeof FONTS)[number]) {
  if (family === DEFAULT_FONT) return t('appearance.font_default', { font: family })
  return kind === 'serif' ? t('appearance.font_serif', { font: family }) : family
}

const MIN_HEIGHT = 480
const DEFAULT_HEIGHT = 720

type Builder = {
  config: EmbedConfig
  filtered: boolean
  method: InstallMethod
  heightInput: string
}

function effectiveConfig({ config, filtered }: Builder) {
  return filtered ? config : withoutFilters(config)
}

function stepFrom(search: string): Step {
  return new URLSearchParams(search).get('step') === 'code' ? 'code' : 'settings'
}

// The builder keeps its state in its own address: the embed's settings, as the
// embed reads them, plus step, method and height, which the embed ignores.
function builderFrom(search: string): Builder {
  const params = new URLSearchParams(search)
  const config = decodeEmbedConfig(search)
  return {
    config,
    filtered: hasFilters(config),
    method: INSTALL_METHODS.find((method) => method === params.get('method')) ?? 'code',
    heightInput: params.get('height') ?? String(DEFAULT_HEIGHT),
  }
}

function builderQuery({ step, builder, defaultColor }: { step: Step; builder: Builder; defaultColor: string }) {
  const params = new URLSearchParams(encodeEmbedConfig(effectiveConfig(builder), { defaultColor }))
  if (builder.method !== 'code') params.set('method', builder.method)
  if (builder.method === 'iframe' && builder.heightInput !== String(DEFAULT_HEIGHT))
    params.set('height', builder.heightInput)
  if (step === 'code') params.set('step', 'code')
  return params.toString()
}

export type EventsMapBuilderProps = {
  feedUrl: string
  crm: string | null
  country: string
  sidebarColor: string | null
  googleMapsApiKey: string | null
  upcomingEventCount: number
  publicSite: string | null
  exampleEventUrl: string | null
  filterOptions: EventFilterOptions
  embedUrl: string
  resizeScriptUrl: string
  indexUrl: string
}

function StepList({ step }: { step: Step }) {
  return (
    <ol
      aria-label={t('steps.label')}
      className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:items-center tw:gap-2 tw:p-0 tw:text-sm tw:font-medium"
    >
      {STEPS.map((item, index) => (
        <li
          key={item}
          aria-current={item === step ? 'step' : undefined}
          className={cn(
            'tw:whitespace-nowrap tw:rounded-full tw:px-3 tw:py-1',
            item === step ? 'tw:bg-primary-50 tw:text-primary-700' : 'tw:text-gray-cool-600'
          )}
        >
          {index + 1}. {t(`steps.${item}`)}
        </li>
      ))}
    </ol>
  )
}

function PublicSiteCallout({
  crm,
  publicSite,
  exampleEventUrl,
}: Pick<EventsMapBuilderProps, 'crm' | 'publicSite' | 'exampleEventUrl'>) {
  if (!publicSite)
    return (
      <Alert type="warning" size="sm" className="tw:text-xs">
        {crm === 'nationbuilder' ? t('events.no_public_site_nationbuilder') : t('events.no_public_site')}
      </Alert>
    )

  return (
    <Alert type="info" size="sm" className="tw:text-xs" icon="external-link">
      {exampleEventUrl
        ? t('events.links_to_example', { example: exampleEventUrl.replace(/^https?:\/\//, '') })
        : t('events.links_to_site', { site: publicSite })}
    </Alert>
  )
}

function TagFilterSettings({
  number,
  filter,
  onChange,
  onRemove,
  events,
}: {
  number: number
  filter: TagFilter
  onChange: (patch: Partial<TagFilter>) => void
  onRemove: () => void
  events: PublicEvent[]
}) {
  const id = useId()
  const heading = filter.name ?? t('tag_filter.heading', { number })
  const options = tagOptions({ events, prefix: filter.prefix })
  const shownCount = options.filter(({ slug }) => !filter.hiddenTags.includes(slug)).length
  const status =
    options.length > 0
      ? t('tag_filter.shown_count', { count: shownCount, total: options.length })
      : filter.prefix
        ? t('tag_filter.no_match', { prefix: filter.prefix })
        : t('events.no_tags')
  const setShown = (slug: string, shown: boolean) =>
    onChange({
      hiddenTags: shown ? filter.hiddenTags.filter((hidden) => hidden !== slug) : [...filter.hiddenTags, slug],
    })

  return (
    <div
      role="group"
      aria-labelledby={`${id}-heading`}
      className="tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-gray-cool-50 tw:p-3"
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <p id={`${id}-heading`} className="tw:m-0 tw:text-sm tw:font-semibold tw:text-gray-900">
          {heading}
        </p>
        <Button
          size="sm"
          rank="secondary"
          color="danger"
          leadingIcon="trash-01"
          ariaLabel={t('tag_filter.remove_label', { name: heading })}
          onClick={onRemove}
        >
          {t('tag_filter.remove')}
        </Button>
      </div>
      <TextInput
        name={`embed_tag_filter_name_${number}`}
        label={t('tag_filter.name')}
        description={t('tag_filter.name_hint')}
        required
        maxLength={MAX_WORDING_LENGTH.tagFilterName}
        value={filter.name ?? ''}
        onChange={(event) => onChange({ name: customWording(event.target.value) })}
      />
      <TextInput
        name={`embed_tag_prefix_${number}`}
        label={t('tag_filter.prefix')}
        description={t('tag_filter.prefix_hint')}
        maxLength={MAX_WORDING_LENGTH.tagPrefix}
        value={filter.prefix ?? ''}
        onChange={(event) => onChange({ prefix: customWording(event.target.value) })}
      />
      <fieldset
        aria-describedby={`${id}-hint`}
        className="tw:m-0 tw:flex tw:min-w-0 tw:flex-col tw:gap-2 tw:border-0 tw:p-0"
      >
        <legend className="tw:mb-1 tw:p-0 tw:text-sm tw:font-medium tw:text-gray-700">{t('tag_filter.options')}</legend>
        <p id={`${id}-hint`} className="tw:m-0 tw:text-sm tw:text-gray-cool-600">
          {t('tag_filter.options_hint')}
        </p>
        {options.length > 0 && (
          <ul className="tw:m-0 tw:max-h-72 tw:list-none tw:overflow-y-auto tw:rounded-lg tw:border tw:border-gray-cool-200 tw:bg-white tw:p-0">
            {options.map((option, index) => (
              <li key={option.slug} className="tw:border-b tw:border-gray-cool-200 tw:px-3 tw:py-2 tw:last:border-b-0">
                <label className="tw:flex tw:min-h-6 tw:cursor-pointer tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-gray-900">
                  <input
                    type="checkbox"
                    aria-describedby={`${id}-${index}`}
                    checked={!filter.hiddenTags.includes(option.slug)}
                    onChange={(event) => setShown(option.slug, event.target.checked)}
                    className="tw:size-4 tw:shrink-0 tw:cursor-pointer tw:accent-primary-600 tw:focus-visible:outline-2 tw:focus-visible:outline-offset-2 tw:focus-visible:outline-primary-500"
                  />
                  {option.name}
                </label>
                <span id={`${id}-${index}`} className="tw:block tw:break-all tw:pl-6 tw:text-xs tw:text-gray-true-500">
                  {option.spelling}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="tw:m-0 tw:text-xs tw:text-gray-true-500" aria-live="polite">
          {status}
        </p>
      </fieldset>
    </div>
  )
}

function TagFiltersSettings({
  filters,
  onChange,
  events,
}: {
  filters: TagFilter[]
  onChange: (filters: TagFilter[]) => void
  events: PublicEvent[]
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div>
        <p className="tw:m-0 tw:text-sm tw:font-medium tw:text-gray-900">{t('filters.tag')}</p>
        <p className="tw:m-0 tw:mt-0.5 tw:text-xs tw:text-gray-true-500">{t('filters.tag_hint')}</p>
      </div>
      {filters.map((filter, position) => (
        <TagFilterSettings
          key={position}
          number={position + 1}
          filter={filter}
          events={events}
          onChange={(patch) =>
            onChange(filters.map((other, index) => (index === position ? { ...other, ...patch } : other)))
          }
          onRemove={() => onChange(filters.filter((_, index) => index !== position))}
        />
      ))}
      {filters.length < MAX_TAG_FILTERS ? (
        <Button size="sm" rank="secondary" leadingIcon="plus" onClick={() => onChange([...filters, NEW_TAG_FILTER])}>
          {t('tag_filter.add')}
        </Button>
      ) : (
        <p className="tw:m-0 tw:text-xs tw:text-gray-true-500">{t('tag_filter.limit', { count: MAX_TAG_FILTERS })}</p>
      )}
    </div>
  )
}

function InstallSettings({
  method,
  onMethodChange,
  heightInput,
  onHeightInputChange,
  heightValid,
  text,
}: {
  method: InstallMethod
  onMethodChange: (method: InstallMethod) => void
  heightInput: string
  onHeightInputChange: (value: string) => void
  heightValid: boolean
  text: string
}) {
  const output = handedOver(method)
  const ready = method !== 'iframe' || heightValid

  return (
    <SettingsSection title={t('install.section')}>
      <RadioGroupField<InstallMethod>
        label={t('install.method')}
        value={method}
        onValueChange={onMethodChange}
        options={INSTALL_METHODS.map((option) => ({
          value: option,
          label: t(`install.${option}`),
          description: t(`install.${option}_hint`),
          whenSelected:
            option === 'iframe' ? (
              <div className="tw:ml-7 tw:mt-3 tw:max-w-48">
                <TextInput
                  name="height"
                  label={t('install.height')}
                  type="number"
                  min={MIN_HEIGHT}
                  description={t('install.height_hint')}
                  errors={
                    heightValid
                      ? {}
                      : { height: { type: 'min', message: t('install.height_too_small', { min: MIN_HEIGHT }) } }
                  }
                  value={heightInput}
                  onChange={(event) => onHeightInputChange(event.target.value)}
                />
              </div>
            ) : undefined,
        }))}
      />
      <EmbedCodeBlock
        label={t(`install.${output}_label`)}
        code={text}
        copyLabel={t(`copy_${output}`)}
        copiedMessage={t(`copied_${output}`)}
        disabled={!ready}
      />
      <p className="tw:m-0 tw:text-sm tw:text-gray-cool-600">{t('install.changes')}</p>
    </SettingsSection>
  )
}

export default function EventsMapBuilder({
  feedUrl,
  crm,
  country,
  sidebarColor,
  googleMapsApiKey,
  upcomingEventCount,
  publicSite,
  exampleEventUrl,
  filterOptions,
  embedUrl,
  resizeScriptUrl,
  indexUrl,
}: EventsMapBuilderProps) {
  const [path, search = ''] = usePage().url.split('?')
  const step = stepFrom(search)
  const [builder, setBuilder] = useState(() => builderFrom(search))
  const { config, filtered, method, heightInput } = builder
  const embedConfig = effectiveConfig(builder)
  const instanceDefault = defaultBrand(sidebarColor)

  const visit = (nextStep: Step, next: Builder, { push }: { push: boolean }) => {
    const query = builderQuery({ step: nextStep, builder: next, defaultColor: instanceDefault })
    const url = query ? `${path}?${query}` : path
    if (push) router.push({ url, preserveState: true })
    else router.replace({ url, preserveState: true, preserveScroll: true })
  }
  const change = (patch: Partial<Builder>) => {
    const next = { ...builder, ...patch }
    setBuilder(next)
    visit(step, next, { push: false })
  }
  const update = (patch: Partial<EmbedConfig>) => change({ config: { ...config, ...patch } })
  const goTo = (next: Step) => visit(next, builder, { push: true })
  const brand = config.color ?? instanceDefault
  const noEvents = upcomingEventCount === 0
  const [showSamples, setShowSamples] = useState(noEvents)
  const [samples] = useState(sampleEvents)
  const feed = useEventsFeed({ feedUrl, config: embedConfig })
  const shown: Pick<EventsFeed, 'events' | 'status' | 'retry'> = showSamples
    ? { events: samples, status: 'ready', retry: feed.retry }
    : feed
  const sampleNote = showSamples
    ? [noEvents ? t('sample.note_empty') : t('sample.note'), config.tagFilters.length > 0 && t('sample.no_tag_filters')]
        .filter(Boolean)
        .join(' ')
    : undefined
  const canSearch = offersLocationSearch({ googleMapsApiKey, feed: shown })
  const offersMap = canSearch && config.layout !== 'grid'

  const height = Number(heightInput)
  const heightValid = Number.isInteger(height) && height >= MIN_HEIGHT
  const address = embedAddress({ embedUrl, query: encodeEmbedConfig(embedConfig, { defaultColor: instanceDefault }) })
  const code = embedCode({
    address,
    resizeScriptUrl,
    title: embedConfig.heading ?? tEmbed('heading'),
    height: method === 'iframe' ? height : null,
  })
  const output = handedOver(method)
  const copyText = output === 'address' ? address : code

  return (
    <PageLayout.Root>
      <PageLayout.Header heading={t('heading')} icon="code-browser" subtitle={t('subtitle')} />
      <PageLayout.Body>
        <EmbedWorkspace
          toolbar={<StepList step={step} />}
          settings={
            step === 'code' ? (
              <InstallSettings
                method={method}
                onMethodChange={(next) => change({ method: next })}
                heightInput={heightInput}
                onHeightInputChange={(next) => change({ heightInput: next })}
                heightValid={heightValid}
                text={copyText}
              />
            ) : (
              <Accordion type="multiple" defaultValue={['layout', 'events', 'search', 'shifts', 'appearance', 'links']}>
                <SettingsAccordionSection value="layout" title={t('sections.layout')}>
                  {!googleMapsApiKey ? (
                    <Alert type="info" size="sm" className="tw:text-xs">
                      {t('layout.no_maps_key')}
                    </Alert>
                  ) : !canSearch ? (
                    <Alert type="info" size="sm" className="tw:text-xs">
                      {t('layout.all_online')}
                    </Alert>
                  ) : (
                    <>
                      <RadioGroupField<Layout>
                        label={t('layout.label')}
                        value={config.layout}
                        onValueChange={(layout) => update({ layout })}
                        options={LAYOUTS.map((layout) => ({
                          value: layout,
                          label: t(`layout.${layout}`),
                          description: t(`layout.${layout}_hint`),
                        }))}
                      />
                      {config.layout === 'both' && (
                        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
                          <span aria-hidden className="tw:text-sm tw:font-medium tw:text-gray-700">
                            {t('layout.default_view')}
                          </span>
                          <ViewSwitch<View>
                            ariaLabel={t('layout.default_view')}
                            value={config.defaultView}
                            onChange={(defaultView) => update({ defaultView })}
                            options={[
                              { value: 'grid', label: tEmbed('view.grid') },
                              { value: 'map', label: tEmbed('view.map') },
                            ]}
                          />
                        </div>
                      )}
                    </>
                  )}
                </SettingsAccordionSection>

                <SettingsAccordionSection value="events" title={t('sections.events')}>
                  <EventFilters
                    filtered={filtered}
                    onFilteredChange={(next) => change({ filtered: next })}
                    filters={config}
                    onFiltersChange={update}
                    options={filterOptions}
                    upcomingEventCount={upcomingEventCount}
                    status={feed.status}
                    matchCount={feed.total}
                  />
                  <PublicSiteCallout crm={crm} publicSite={publicSite} exampleEventUrl={exampleEventUrl} />
                </SettingsAccordionSection>

                <SettingsAccordionSection value="search" title={t('sections.search')}>
                  {!googleMapsApiKey ? (
                    <Alert type="info" size="sm" className="tw:text-xs">
                      {t('search.no_maps_key')}
                    </Alert>
                  ) : (
                    !canSearch && (
                      <Alert type="info" size="sm" className="tw:text-xs">
                        {t('search.all_online')}
                      </Alert>
                    )
                  )}
                  <RadioGroupField
                    name="embed_radius"
                    label={t('search.radius')}
                    description={t('search.radius_hint')}
                    disabled={!canSearch}
                    value={String(config.radius)}
                    onValueChange={(radius) => update({ radius: Number(radius) as Radius })}
                    options={RADIUS_OPTIONS.map((radius) => ({
                      value: String(radius),
                      label: t('search.radius_option', { radius }),
                    }))}
                  />
                  <ToggleRow
                    label={t('search.nearest')}
                    description={t('search.nearest_hint')}
                    checked={config.showNearest}
                    onCheckedChange={(value) => update({ showNearest: value })}
                    disabled={!canSearch}
                  />
                  <ToggleRow
                    label={t('filters.date')}
                    description={t('filters.date_hint')}
                    checked={config.dateFilter}
                    onCheckedChange={(value) => update({ dateFilter: value })}
                  />
                  <ToggleRow
                    label={t('filters.online')}
                    description={t('filters.online_hint')}
                    checked={config.onlineFilter}
                    onCheckedChange={(value) => update({ onlineFilter: value })}
                  />
                  <ToggleRow
                    label={t('filters.event_type')}
                    description={t('filters.event_type_hint')}
                    checked={config.typeFilter}
                    onCheckedChange={(value) => update({ typeFilter: value })}
                  />
                  <TagFiltersSettings
                    filters={config.tagFilters}
                    onChange={(tagFilters) => update({ tagFilters })}
                    events={feed.events}
                  />
                </SettingsAccordionSection>

                <SettingsAccordionSection value="shifts" title={t('sections.shifts')}>
                  <ToggleRow
                    label={t('shifts.coverage')}
                    description={t('shifts.coverage_hint')}
                    checked={config.shifts}
                    onCheckedChange={(value) => update({ shifts: value })}
                  />
                  <ToggleRow
                    label={t('shifts.needs_volunteers')}
                    description={t('shifts.needs_volunteers_hint')}
                    checked={config.needsVolunteers}
                    onCheckedChange={(value) => update({ needsVolunteers: value })}
                    disabled={!config.shifts}
                  />
                </SettingsAccordionSection>

                <SettingsAccordionSection value="appearance" title={t('sections.appearance')}>
                  <ColorField
                    label={tBrand('label')}
                    value={brand}
                    defaultColor={instanceDefault}
                    onChange={(color) => update({ color })}
                  />
                  {!isReadableOnWhite(brand) && (
                    <Alert type="warning" size="sm" className="tw:text-xs">
                      {tBrand('low_contrast')}
                    </Alert>
                  )}
                  <ToggleRow
                    label={t('appearance.show_event_type')}
                    description={t('appearance.show_event_type_hint')}
                    checked={config.showEventType}
                    onCheckedChange={(value) => update({ showEventType: value })}
                  />
                  <ToggleRow
                    label={t('appearance.show_intro')}
                    description={t('appearance.show_intro_hint')}
                    checked={config.showIntro}
                    onCheckedChange={(value) => update({ showIntro: value })}
                  />
                  <ToggleRow
                    label={t('appearance.pin')}
                    description={offersMap ? t('appearance.pin_hint') : t('appearance.pin_no_map')}
                    checked={config.pin !== null}
                    onCheckedChange={(on) => update({ pin: on ? FIRST_PIN_COLOR : null })}
                    disabled={!offersMap}
                  />
                  {offersMap && config.pin !== null && (
                    <ColorField
                      label={t('appearance.pin_colour')}
                      value={config.pin}
                      defaultColor={brand}
                      onChange={(pin) => update({ pin })}
                    />
                  )}
                  <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
                    <span aria-hidden className="tw:text-sm tw:font-medium tw:text-gray-700">
                      {t('appearance.corners')}
                    </span>
                    <ViewSwitch<Corners>
                      ariaLabel={t('appearance.corners')}
                      value={config.corners}
                      onChange={(corners) => update({ corners })}
                      options={[
                        { value: 'rounded', label: t('appearance.corners_rounded') },
                        { value: 'square', label: t('appearance.corners_square') },
                      ]}
                    />
                  </div>
                  <SelectField
                    name="embed_font"
                    label={t('appearance.font')}
                    description={t('appearance.font_hint')}
                    value={config.font}
                    onValueChange={(font) => update({ font })}
                    options={FONTS.map((font) => ({ value: font.family, label: fontLabel(font) }))}
                  />
                </SettingsAccordionSection>

                <SettingsAccordionSection value="links" title={t('sections.links')}>
                  <TextInput
                    name="embed_heading"
                    label={t('wording.heading')}
                    description={t('wording.heading_hint', {
                      default: canSearch ? tEmbed('search.heading') : tEmbed('heading'),
                    })}
                    maxLength={MAX_WORDING_LENGTH.heading}
                    value={config.heading ?? ''}
                    onChange={(event) => update({ heading: customWording(event.target.value) })}
                  />
                  <TextInput
                    name="embed_placeholder"
                    label={t('wording.placeholder')}
                    description={t('wording.placeholder_hint', { default: tEmbed('search.placeholder') })}
                    maxLength={MAX_WORDING_LENGTH.placeholder}
                    disabled={!canSearch}
                    value={config.placeholder ?? ''}
                    onChange={(event) => update({ placeholder: customWording(event.target.value) })}
                  />
                  <TextInput
                    name="embed_cta"
                    label={t('wording.cta')}
                    description={t('wording.cta_hint', { default: tEmbed('rsvp') })}
                    maxLength={MAX_WORDING_LENGTH.cta}
                    value={config.cta ?? ''}
                    onChange={(event) => update({ cta: customWording(event.target.value) })}
                  />
                  <RadioGroupField<LinkTarget>
                    label={t('links.label')}
                    value={config.links}
                    onValueChange={(value) => update({ links: value })}
                    options={(['new', 'top'] as const).map((option) => ({
                      value: option,
                      label: t(`links.${option}`),
                      description: t(`links.${option}_hint`),
                    }))}
                  />
                </SettingsAccordionSection>
              </Accordion>
            )
          }
          preview={
            <PreviewShell
              host={t('preview_host')}
              sampleSwitch={{ checked: showSamples, onCheckedChange: setShowSamples, label: t('sample.label') }}
              note={sampleNote}
            >
              <EmbedErrorBoundary>
                <EventsEmbed
                  headingLevel={2}
                  config={embedConfig}
                  brand={brand}
                  feed={shown}
                  country={country}
                  googleMapsApiKey={googleMapsApiKey}
                />
              </EmbedErrorBoundary>
            </PreviewShell>
          }
        />
      </PageLayout.Body>
      <PageLayout.Footer>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          {step === 'code' ? (
            <>
              <Button size="sm" rank="secondary" leadingIcon="arrow-narrow-left" onClick={() => goTo('settings')}>
                {t('back')}
              </Button>
              <div className="tw:ml-auto">
                <Link as="inertiaLink" href={indexUrl} size="sm">
                  {t('done')}
                </Link>
              </div>
            </>
          ) : (
            <div className="tw:ml-auto">
              <Button size="sm" trailingIcon="arrow-narrow-right" onClick={() => goTo('code')}>
                {t('next')}
              </Button>
            </div>
          )}
        </div>
      </PageLayout.Footer>
    </PageLayout.Root>
  )
}
